import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { auth } from "config/firebase";
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";

import ResetPassword from "./sections/ResetPassword";

function AuthActions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<'success' | 'error' | 'info'>('info');
  const navigate = useNavigate();
  const location = useLocation();

  const [values, setValues] = useState({
    newPassword: "",
    confirmPassword: "",
    mode: "",
    actionCode: "",
    continueUrl: "",
    lang: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setParams();
  }, []);

  const setParams = () => {
    const { search } = location;
    const params = new URLSearchParams(search);

    // Get the action to complete.
    const mode = params.get("mode");
    // Get the one-time code from the query parameter.
    const actionCode = params.get("oobCode");
    // (Optional) Get the continue URL from the query parameter if available.
    const _continueUrl = params.get("continueUrl");
    // (Optional) Get the language code if available.
    const _lang = params.get("lang") || "en";
    setValues({
      ...values,
      mode: mode || "",
      actionCode: actionCode || "",
      continueUrl: _continueUrl || "",
      lang: _lang,
    });
  };

  function handleVerifyEmail() {
    const { actionCode } = values;

    // Try to apply the email verification code.
    applyActionCode(auth, actionCode)
      .then((res) => {
        console.log("response", res);
        // Email address has been verified.
        // TODO: Display a confirmation message to the user.
        setMsg("Email Verified Successfully");
        setMsgType('success');
        return navigate("/home");
        // You could also provide the user with a link back to the app.
        // TODO: If a continue URL is available, display a button which on
        // click redirects the user back to the app via continueUrl with
        // additional state determined from that URL's parameters.
      })
      .catch((error) => {
        setMsg(error.message);
        setMsgType('error');
        console.log("error", error);
        // Code is invalid or expire10:17 PMd. Ask the user to verify their email address again
      });
  }

  function handleResetPassword(newPass: string, confirmPass: string) {
    setIsLoading(true);
    setMsg("");
    if (newPass.length < 6) {
      setMsgType('error');
      setIsLoading(false);
      return setMsg("Password must be at least 6 characters long");
    }
    if (newPass !== confirmPass) {
      setMsgType('error');
      setIsLoading(false);
      return setMsg("Passwords don't match");
    }

    // Using auth from firebase config

    // Verify the password reset code is valid.
    verifyPasswordResetCode(auth, values.actionCode)
      .then(() => {
        // Save the new password.
        confirmPasswordReset(auth, values.actionCode, newPass)
          .then(() => {
            setMsg("Success! Please login with your new password!");
            setMsgType('success');

            setTimeout(() => {
              navigate("/");
            }, 1200);

            // Password reset has been confirmed and new password updated.
            // TODO: Display a link back to the app, or sign-in the user directly
            // if the page belongs to the same domain as the app:
            // auth.signInWithEmailAndPassword(email, newPass);
            // TODO: If a continue URL is available, display a button which on
            // click redirects the user back to the app via continueUrl with
            // additional state determined from that URL's parameters.
          })
          .catch((error) => {
            console.log(error);
            if (error.code === "auth/weak-password") {
              setMsg("Password must be at least 6 characters long");
            } else {
              setMsg(error.message);
            }
            setMsgType('error');
            // Error occurred during confirmation. The code might have expired or the
            // password is too weak.
          });
      })
      .catch((error) => {
        console.log("action error", error);
        setMsg(error.message);
        setMsgType('error');
        // Invalid or expired action code. Ask user to try to reset the password
        // again.
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  // todo: add forgot email functionality
  // function handleRecoverEmail(auth, actionCode, _lang) {
  //   // Localize the UI to the selected language as determined by the lang
  //   // parameter.
  //   let restoredEmail = null;
  //   // Confirm the action code is valid.
  //   auth
  //     .checkActionCode(actionCode)
  //     .then((info) => {
  //       // Get the restored email address.
  //       restoredEmail = info.data.email;

  //       // Revert to the old email.
  //       return auth.applyActionCode(actionCode);
  //     })
  //     .then(() => {
  //       // Account email reverted to restoredEmail

  //       // TODO: Display a confirmation message to the user.

  //       // You might also want to give the user the option to reset their password
  //       // in case the account was compromised:
  //       auth
  //         .sendPasswordResetEmail(restoredEmail)
  //         .then(() => {
  //           // Password reset confirmation sent. Ask user to check their email.
  //         })
  //         .catch((_error) => {
  //           // Error encountered while sending password reset code.
  //         });
  //     })
  //     .catch((_error) => {
  //       // Invalid code.
  //     });
  // }

  const getPageTitle = () => {
    switch (values.mode) {
      case 'verifyEmail':
        return 'Email Verification';
      case 'resetPassword':
        return 'Reset Password';
      default:
        return 'Account Actions';
    }
  };

  const getPageIcon = () => {
    switch (values.mode) {
      case 'verifyEmail':
        return <EmailIcon color="primary" fontSize={isMobile ? "large" : "inherit"} />;
      case 'resetPassword':
        return <SecurityIcon color="primary" fontSize={isMobile ? "large" : "inherit"} />;
      default:
        return <SecurityIcon color="primary" fontSize={isMobile ? "large" : "inherit"} />;
    }
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            color: 'primary.main',
            fontWeight: 600
          }}
        >
          {getPageIcon()}
          {getPageTitle()}
        </Typography>
        <Typography 
          variant="body1" 
          color="textSecondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Complete your account verification or password reset
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        {values.mode === "verifyEmail" && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Verifying Email...
            </Typography>
            {handleVerifyEmail()}
          </Box>
        )}

        {values.mode === "resetPassword" && (
          <ResetPassword
            handleResetPassword={(newPassword: string, confirmPassword: string) => {
              handleResetPassword(newPassword, confirmPassword);
            }}
            isLoading={isLoading}
          />
        )}

        {/* Message Display */}
        {msg && (
          <Alert 
            severity={msgType}
            sx={{ 
              mt: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            {msg}
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export default AuthActions;
